// @ts-ignore
import { loadStripe, Stripe } from '@stripe/stripe-js'
import { getApiBaseUrl } from './apiBaseUrl'

let stripePromise: Promise<Stripe | null>

const getStripeApiUrl = () => getApiBaseUrl()

export interface VerifiedCheckoutSession {
  paid: boolean
  session_id: string
  tier?: string
  value: number
  currency: string
}

// Get Stripe configuration from backend
export const getStripeConfig = async () => {
  const API_BASE_URL = getStripeApiUrl()

  const response = await fetch(`${API_BASE_URL}/api/stripe/config`)
  if (!response.ok) {
    throw new Error('Failed to fetch Stripe configuration')
  }
  return response.json()
}

// Initialize Stripe
export const getStripe = () => {
  if (!stripePromise) {
    stripePromise = getStripeConfig().then(config =>
      loadStripe(config.publishable_key)
    )
  }
  return stripePromise
}

// Create checkout session
export const createCheckoutSession = async (
  token: string,
  tier: 'basic' | 'premium' | 'instant' | 'day_trial' | 'basic_exit' | 'premium_exit' | 'instant_exit' = 'premium',
  billingInterval: 'month' | 'year' = 'month'
) => {
  const API_BASE_URL = getStripeApiUrl()
  const body = { tier, billing_interval: billingInterval }

  const response = await fetch(`${API_BASE_URL}/api/stripe/create-checkout-session`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to create checkout session')
  }

  return response.json()
}

export const verifyCheckoutSession = async (
  token: string,
  sessionId: string
): Promise<VerifiedCheckoutSession> => {
  const API_BASE_URL = getStripeApiUrl()
  const params = new URLSearchParams({ session_id: sessionId })

  const response = await fetch(`${API_BASE_URL}/api/stripe/verify-checkout-session?${params}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to verify checkout session')
  }

  return response.json()
}

export const createPortalSession = async (token: string) => {
  const API_BASE_URL = getStripeApiUrl()

  const response = await fetch(`${API_BASE_URL}/api/stripe/create-portal-session`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    let message = 'Failed to create billing portal session'
    try {
      const error = await response.json()
      message = error.message || message
    } catch (_) {}
    throw new Error(message)
  }

  return response.json()
}

export const createDonationCheckout = async (token: string, amountCents: number) => {
  const API_BASE_URL = getStripeApiUrl()

  const response = await fetch(`${API_BASE_URL}/api/stripe/create-donation-checkout`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ amount_cents: amountCents }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to create donation checkout')
  }

  return response.json()
}

// Sync subscription from Stripe (useful when webhooks don't work)
export const syncSubscription = async (token: string) => {
  const API_BASE_URL = getStripeApiUrl()

  const response = await fetch(`${API_BASE_URL}/api/stripe/sync-subscription`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.message || 'Failed to sync subscription')
  }

  return response.json()
}

// Upgrade subscription to a new tier
export const upgradeSubscription = async (
  token: string,
  tier: 'basic' | 'premium' | 'instant',
  billingInterval: 'month' | 'year' = 'month'
) => {
  const API_BASE_URL = getStripeApiUrl()

  const response = await fetch(`${API_BASE_URL}/api/stripe/upgrade-subscription`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ tier, billing_interval: billingInterval }),
  })

  if (!response.ok) {
    let message = 'Failed to upgrade subscription'
    try {
      const error = await response.json()
      message = error.message || message
    } catch (_) {}
    throw new Error(message)
  }

  return response.json()
}
